// src/components/chatPolicy/ProtectionCard.jsx
// Una protección: edita target + lista de allowedSenders. Cada sender es una
// fila con puestos/unidades/sucursales/paises/users + checks sameUnidad/sameSucursal/samePais.
import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import MultiSelectChips from './MultiSelectChips';
import UserTypeahead    from './UserTypeahead';
import PolicyImpactBadge from './PolicyImpactBadge';
import { updateProtection, deleteProtection } from '../../services/chatPolicyApi';

function emptySender() {
    return {
        puestos: [], unidades: [], sucursales: [], paises: [], users: [],
        sameUnidad: false, sameSucursal: false, samePais: false,
    };
}

function normalizeSenders(arr) {
    return (arr || []).map(s => ({
        puestos:      s.puestos    || [],
        unidades:     s.unidades   || [],
        sucursales:   s.sucursales || [],
        paises:       s.paises     || [],
        users:        s.users      || [],
        sameUnidad:   !!s.sameUnidad,
        sameSucursal: !!s.sameSucursal,
        samePais:     !!s.samePais,
    }));
}

function normalizeTarget(t) {
    return {
        puestos:    t?.puestos    || [],
        unidades:   t?.unidades   || [],
        sucursales: t?.sucursales || [],
        paises:     t?.paises     || [],
        users:      t?.users      || [],
    };
}

export default function ProtectionCard({ protection, catalog, onUpdated, onRemoved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft]     = useState(() => ({
        description: protection.description || '',
        enabled: protection.enabled,
        target:  normalizeTarget(protection.target),
        allowedSenders: normalizeSenders(protection.allowedSenders),
    }));
    const [saving, setSaving]   = useState(false);

    const cancel = () => {
        setDraft({
            description: protection.description || '',
            enabled: protection.enabled,
            target:  normalizeTarget(protection.target),
            allowedSenders: normalizeSenders(protection.allowedSenders),
        });
        setEditing(false);
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                description: draft.description,
                enabled: draft.enabled,
                target: {
                    ...draft.target,
                    users: draft.target.users.map(u => u._id),
                },
                allowedSenders: draft.allowedSenders.map(s => ({
                    ...s,
                    users: (s.users || []).map(u => u._id),
                })),
            };
            const updated = await updateProtection(protection._id, payload);
            onUpdated?.(updated);
            setEditing(false);
            toast.success('Protección actualizada');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo guardar');
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!window.confirm('¿Eliminar esta protección?')) return;
        try {
            await deleteProtection(protection._id);
            onRemoved?.(protection._id);
            toast.success('Protección eliminada');
        } catch (e) {
            toast.error(e.response?.data?.error || 'No se pudo eliminar');
        }
    };

    const addSender = () => setDraft(d => ({ ...d, allowedSenders: [...d.allowedSenders, emptySender()] }));
    const removeSender = (idx) => setDraft(d => ({ ...d, allowedSenders: d.allowedSenders.filter((_, i) => i !== idx) }));
    const updateSender = (idx, patch) => setDraft(d => ({
        ...d,
        allowedSenders: d.allowedSenders.map((s, i) => i === idx ? { ...s, ...patch } : s),
    }));

    const targetCriteriaForPreview = () => ({
        puestos:    draft.target.puestos,
        unidades:   draft.target.unidades,
        sucursales: draft.target.sucursales,
        paises:     draft.target.paises,
        users:      (draft.target.users || []).map(u => u._id),
    });
    const senderCriteriaForPreview = (s) => ({
        puestos:    s.puestos,
        unidades:   s.unidades,
        sucursales: s.sucursales,
        paises:     s.paises,
        users:      (s.users || []).map(u => u._id),
    });

    const targetSummary = (t) => {
        const parts = [];
        if (t.puestos?.length)    parts.push(`Puestos: ${t.puestos.join(', ')}`);
        if (t.unidades?.length)   parts.push(`Unidades: ${t.unidades.join(', ')}`);
        if (t.sucursales?.length) parts.push(`Sucursales: ${t.sucursales.join(', ')}`);
        if (t.paises?.length)     parts.push(`Países: ${t.paises.join(', ')}`);
        if (t.users?.length)      parts.push(`Usuarios: ${t.users.map(u => u.name).join(', ')}`);
        return parts;
    };
    const senderSummary = (s) => {
        const parts = [];
        if (s.puestos?.length)    parts.push(<>Puestos: <strong>{s.puestos.join(', ')}</strong></>);
        if (s.unidades?.length)   parts.push(<>Unidades: <strong>{s.unidades.join(', ')}</strong></>);
        if (s.sucursales?.length) parts.push(<>Sucursales: <strong>{s.sucursales.join(', ')}</strong></>);
        if (s.paises?.length)     parts.push(<>Países: <strong>{s.paises.join(', ')}</strong></>);
        if (s.users?.length)      parts.push(<>Usuarios: <strong>{s.users.map(u => u.name).join(', ')}</strong></>);
        if (s.sameUnidad)         parts.push(<>Misma unidad ✓</>);
        if (s.sameSucursal)       parts.push(<>Misma sucursal ✓</>);
        if (s.samePais)           parts.push(<>Mismo país ✓</>);
        return parts;
    };

    return (
        <div style={{ background: 'var(--c-card)', border: '1px solid var(--c-border)', borderRadius: 12, padding: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                {editing ? (
                    <Form.Control
                        size="sm"
                        value={draft.description}
                        onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
                        placeholder="Descripción de la regla"
                        style={{ borderRadius: 8, maxWidth: 400 }}
                    />
                ) : (
                    <h6 style={{ fontWeight: 700, margin: 0 }}>
                        {protection.description || '(Sin descripción)'}
                        {!protection.enabled && <span style={{ fontSize: 11, color: '#b91c1c', marginLeft: 8 }}>(deshabilitada)</span>}
                    </h6>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                    {!editing && (
                        <>
                            <Button size="sm" variant="outline-secondary" onClick={() => setEditing(true)}>Editar</Button>
                            <Button size="sm" variant="outline-danger" onClick={remove}>Eliminar</Button>
                        </>
                    )}
                </div>
            </div>

            {editing && (
                <Form.Check
                    type="switch"
                    label="Habilitada"
                    checked={draft.enabled}
                    onChange={e => setDraft(d => ({ ...d, enabled: e.target.checked }))}
                    style={{ marginBottom: 8 }}
                />
            )}

            <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-3)', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Destinatarios protegidos</span>
                    {editing && <PolicyImpactBadge mode="target" criteria={targetCriteriaForPreview()} />}
                </div>
                {editing ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div>
                            <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Puestos</label>
                            <MultiSelectChips
                                options={catalog?.puestos || []}
                                value={draft.target.puestos}
                                onChange={v => setDraft(d => ({ ...d, target: { ...d.target, puestos: v } }))}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Unidades</label>
                            <MultiSelectChips
                                options={catalog?.unidades || []}
                                value={draft.target.unidades}
                                onChange={v => setDraft(d => ({ ...d, target: { ...d.target, unidades: v } }))}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Sucursales</label>
                            <MultiSelectChips
                                options={catalog?.sucursales || []}
                                value={draft.target.sucursales}
                                onChange={v => setDraft(d => ({ ...d, target: { ...d.target, sucursales: v } }))}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Países</label>
                            <MultiSelectChips
                                options={catalog?.paises || []}
                                value={draft.target.paises}
                                onChange={v => setDraft(d => ({ ...d, target: { ...d.target, paises: v } }))}
                            />
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                            <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Usuarios específicos</label>
                            <UserTypeahead
                                catalog={catalog}
                                value={draft.target.users}
                                onChange={v => setDraft(d => ({ ...d, target: { ...d.target, users: v } }))}
                            />
                        </div>
                    </div>
                ) : (
                    <div style={{ fontSize: 13 }}>
                        {targetSummary(protection.target || {}).length > 0
                            ? targetSummary(protection.target).map((p, i) => <div key={i}>{p}</div>)
                            : <span style={{ color: 'var(--c-text-4)', fontStyle: 'italic' }}>(sin destinatarios — protección inactiva)</span>}
                    </div>
                )}
            </div>

            <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--c-text-3)', marginBottom: 4 }}>
                    Remitentes autorizados {editing && <Button size="sm" variant="link" onClick={addSender} style={{ padding: 0, fontSize: 11 }}>+ añadir regla</Button>}
                </div>
                {draft.allowedSenders.length === 0 && !editing && (
                    <div style={{ color: 'var(--c-text-4)', fontStyle: 'italic', fontSize: 13 }}>(nadie más allá de admin global)</div>
                )}
                {(editing ? draft.allowedSenders : protection.allowedSenders || []).map((s, i) => (
                    <div key={i} style={{ border: '1px dashed var(--c-border)', borderRadius: 8, padding: 8, marginBottom: 6, fontSize: 13 }}>
                        {editing ? (
                            <>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                                    <PolicyImpactBadge mode="sender" criteria={senderCriteriaForPreview(s)} />
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                                    <div>
                                        <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Puestos</label>
                                        <MultiSelectChips options={catalog?.puestos || []} value={s.puestos} onChange={v => updateSender(i, { puestos: v })} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Unidades</label>
                                        <MultiSelectChips options={catalog?.unidades || []} value={s.unidades} onChange={v => updateSender(i, { unidades: v })} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Sucursales</label>
                                        <MultiSelectChips options={catalog?.sucursales || []} value={s.sucursales} onChange={v => updateSender(i, { sucursales: v })} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Países</label>
                                        <MultiSelectChips options={catalog?.paises || []} value={s.paises} onChange={v => updateSender(i, { paises: v })} />
                                    </div>
                                </div>
                                <div style={{ marginBottom: 6 }}>
                                    <label style={{ fontSize: 11, color: 'var(--c-text-4)' }}>Usuarios específicos</label>
                                    <UserTypeahead catalog={catalog} value={s.users} onChange={v => updateSender(i, { users: v })} />
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                        <Form.Check type="checkbox" label="Misma unidad que el destinatario" checked={s.sameUnidad}
                                            onChange={e => updateSender(i, { sameUnidad: e.target.checked })} />
                                        <Form.Check type="checkbox" label="Misma sucursal" checked={s.sameSucursal}
                                            onChange={e => updateSender(i, { sameSucursal: e.target.checked })} />
                                        <Form.Check type="checkbox" label="Mismo país" checked={s.samePais}
                                            onChange={e => updateSender(i, { samePais: e.target.checked })} />
                                    </div>
                                    <Button size="sm" variant="link" className="text-danger" onClick={() => removeSender(i)} style={{ padding: 0 }}>Quitar regla</Button>
                                </div>
                            </>
                        ) : (
                            <div>{senderSummary(s).map((p, j) => <span key={j}>{p}{j < senderSummary(s).length - 1 ? ' · ' : ''}</span>)}</div>
                        )}
                    </div>
                ))}
            </div>

            {editing && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                    <Button size="sm" variant="light" onClick={cancel}>Cancelar</Button>
                    <Button size="sm" variant="success" onClick={save} disabled={saving}>
                        {saving ? 'Guardando…' : 'Guardar'}
                    </Button>
                </div>
            )}
        </div>
    );
}
